import { c as _c } from "react/compiler-runtime";
import "zod/compile";
import "@/styles/globals.css";
import type { AppProps } from "next/app";
import type { NextPageWithLayout } from "@/types/next";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { ThemeProvider } from "@/components/theme-provider";
import { QueryClient, QueryClientProvider, useQueryClient } from "react-query";
import { AuthProvider, SessionExpirationModal } from "@/components/AuthProvider";
import { GitHubRateLimitError } from "@/components/GitHubRateLimitError";
import { GithubSsoReauthorizationError } from "@/components/GithubSsoReauthorizationError";
import { SsoBlockedOrgGate } from "@/components/SsoBlockedOrgGate";
import { GitHubRateLimitException, GithubSsoReauthorizationRequiredException } from "@/hooks/useConnectedGithubOrgs";
import { ErrorBoundary } from "react-error-boundary";
import type { AxiosError } from "axios";
import { GlobalErrorSplash } from "@/components/GlobalErrorSplash";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ReactQueryDevtools } from "react-query/devtools";
import * as Sentry from "@sentry/nextjs";
import { useEffect, useRef, useState } from "react";
import { Router, useRouter } from "next/router";
import localFont from "next/font/local";
import { IBM_Plex_Sans, Inter } from "next/font/google";
import Script from "next/script";
import { cn } from "@/lib/utils";
import { SpeedInsights } from "@vercel/speed-insights/next";
import { useAnvilPrefetch } from "@/hooks/useAnvilPrefetch";
import { useWebVitalsReporting } from "@/hooks/useWebVitalsReporting";
import { markNavigationRenderError, useNavigationTimingReporting } from "@/hooks/useNavigationTimingReporting";
import { useFrontendVersionRefresh } from "@/hooks/useFrontendVersionRefresh";
import { Dv360FloodlightGlobal } from "@/components/marketing/Dv360FloodlightGlobal";
import { isProductionOrStaging } from "@/lib/environment";
import { captureAppFirstTouchUtmCampaignFromUrl, captureAppFirstTouchRdtCidFromUrl } from "@/lib/appFirstTouchAttribution";
import { clearOauthSuccessAppLoadGtmSessionFlag } from "@/lib/oauthSuccessAppLoadGtm";
import { clearCodesmithLayoutCache } from "@/components/sidebar/useCodesmithEnabledCache";
import { useMcpOAuthResultListener } from "@/hooks/useMcpServers";
import { useCodesmithIntroVideoPrefetch } from "@/components/codesmith/CodesmithIntroVideoModal";
import { AppearanceSettingsSync } from "@/components/AppearanceSettingsSync";
import { CODESMITH_EXTENSION_AUTH_MESSAGE, isCodesmithExtensionEmbed } from "@/lib/codesmithExtensionEmbed";
import { OrgUrlSyncProvider } from "@/components/OrgUrlSyncProvider";
const berkeleyMonoVariable = localFont({
  src: "../../public/BerkeleyMonoVariable.woff2"
});
const inter = Inter({
  subsets: ["latin"],
  weight: ["100", "200", "300", "400", "500", "600"]
});
const ibmPlexSans = IBM_Plex_Sans({
  subsets: ["latin"],
  weight: ["100", "200", "300", "400", "500", "600"]
});
const departureMono = localFont({
  src: "../../public/DepartureMono-Regular.woff2"
});
if (typeof window !== "undefined") {
  // Add global error handlers for Sentry
  window.onerror = (message, source, lineno, colno, error) => {
    Sentry.captureException(error, {
      extra: {
        message,
        source,
        lineno,
        colno
      }
    });
  };

  // Capture all unhandled promise rejections
  window.onunhandledrejection = event => {
    Sentry.captureException(event.reason, {
      extra: {
        type: "unhandledrejection",
        reason: event.reason
      }
    });
  };
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1
    }
  }
});
function ErrorPage(t0) {
  const $ = _c(15);
  const {
    error,
    resetErrorBoundary
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== queryClient) {
    t1 = () => {
      try {
        queryClient.clear();
      } catch {}
      try {
        window.sessionStorage.removeItem("auth-had-session");
      } catch {}
      clearOauthSuccessAppLoadGtmSessionFlag();
      clearCodesmithLayoutCache();
      posthog?.reset();
      window.location.href = `${process.env.NEXT_PUBLIC_API_URL}/logout`;
    };
    $[0] = queryClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleLogout = t1;
  if (error instanceof GitHubRateLimitException) {
    let t2;
    if ($[2] !== queryClient || $[3] !== resetErrorBoundary) {
      t2 = () => {
        queryClient.invalidateQueries("connectedGithubOrgs");
        resetErrorBoundary();
      };
      $[2] = queryClient;
      $[3] = resetErrorBoundary;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== error.rateLimitReset || $[6] !== error.rateLimitResetSeconds || $[7] !== t2) {
      t3 = <GitHubRateLimitError rateLimitReset={error.rateLimitReset} rateLimitResetSeconds={error.rateLimitResetSeconds} onRetry={t2} />;
      $[5] = error.rateLimitReset;
      $[6] = error.rateLimitResetSeconds;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  if (error instanceof GithubSsoReauthorizationRequiredException) {
    let t2;
    if ($[9] !== queryClient || $[10] !== resetErrorBoundary) {
      t2 = <GithubSsoReauthorizationError onRetry={() => {
        queryClient.invalidateQueries("connectedGithubOrgs");
        resetErrorBoundary();
      }} />;
      $[9] = queryClient;
      $[10] = resetErrorBoundary;
      $[11] = t2;
    } else {
      t2 = $[11];
    }
    return t2;
  }
  const axiosError = error as AxiosError;
  if (axiosError.response?.status === 401) {
    let t2;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <SessionExpirationModal open={true} setOpen={_temp} />;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  const data = queryClient.getQueryData("connectedGithubOrgs");
  const activeGithubOrg = (data as {
    active_org_name: string | null;
  })?.active_org_name ?? null;
  const user = queryClient.getQueryData("user") as {
    id: string;
    name: string;
    email: string;
  } | undefined;
  Sentry.withScope(scope => {
    if (user?.id && user?.name) {
      scope.setUser({
        id: user.id,
        username: user.name,
        email: user.email || undefined
      });
    }
    if (activeGithubOrg && typeof activeGithubOrg === "string") {
      scope.setTag("organization", activeGithubOrg);
      scope.setExtra("activeGithubOrg", activeGithubOrg);
    }
    if (axiosError.response) {
      scope.setExtra("error_response", axiosError.response.data);
      scope.setExtra("error_status", axiosError.response.status);
      scope.setExtra("error_headers", axiosError.response.headers);
    }
    if (axiosError.config) {
      scope.setExtra("error_config", axiosError.config);
    }
    const status = axiosError.response?.status;
    if (status && status >= 500) {
      scope.setLevel("error");
    } else {
      if (status && status >= 400) {
        scope.setLevel("warning");
      }
    }
    Sentry.captureException(error);
  });
  posthog?.capture("error", {
    error,
    orgName: activeGithubOrg,
    stackTrace: error?.stack || undefined
  });
  let t2;
  if ($[13] !== handleLogout) {
    t2 = <GlobalErrorSplash onLogout={handleLogout} />;
    $[13] = handleLogout;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

// Rendered inside QueryClientProvider (hooks in App itself run outside it).
function _temp() {}
function McpOAuthResultBridge() {
  useMcpOAuthResultListener();
  return null;
}
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
export default function App({
  Component,
  pageProps
}: AppPropsWithLayout) {
  const oldUrlRef = useRef("");
  const router = useRouter();
  const extensionEmbed = isCodesmithExtensionEmbed(router.query.embed) || typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "extension";

  // Check if this is the copy-prompt route (public, no auth required)
  // Compute synchronously to avoid race condition where AuthProvider wraps the page on first render
  const isCopyPromptRoute = router.pathname === "/copy-prompt" || router.pathname === "/[org]/copy-prompt";

  // The canvas sandbox frame runs agent-authored code in an opaque origin
  // (sandboxed iframe): no auth, and no analytics — cookies and storage are
  // unavailable there anyway.
  const isCanvasSandboxRoute = router.pathname === "/canvas-sandbox";
  useEffect(() => {
    if (window.location.pathname === "/canvas-sandbox") return;

    // Read toolbar parameter before Next.js can override the hash
    // See: https://posthog.com/docs/toolbar#1-the-toolbar-is-being-overridden
    const toolbarJSON = new URLSearchParams(window.location.hash.substring(1)).get("__posthog");
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
      // Enable debug mode
      loaded: posthog => {
        if (process.env.NODE_ENV === "development") {
          posthog.debug();
        }
        if (toolbarJSON) {
          try {
            const config = JSON.parse(toolbarJSON);
            // Only load if it's a valid PostHog toolbar config
            if (config && typeof config === "object" && config.action === "ph_authorize") {
              posthog.loadToolbar(config);
            }
          } catch (e) {
            // Ignore invalid JSON in hash parameter
            if (process.env.NODE_ENV === "development") {
              console.warn("PostHog toolbar failed to load: Invalid JSON in hash parameter", e);
            }
          }
        }
      },
      // Site apps are scripts configured in the PostHog project that inject
      // their own UI (badges, banners) into every page that loads this key —
      // including localhost. Keep them out of dev so tracking never draws
      // over the app while developing; the toolbar still loads explicitly
      // via the #__posthog hash above.
      opt_in_site_apps: process.env.NODE_ENV !== "development",
      // Feature gating lives in the backend (installation columns + the
      // `rollouts` table, see backend/docs/FEATURE_FLAGS.md), so nothing here
      // ever reads a PostHog flag. This sends `disable_flags` on the /flags
      // request, so the response still carries remote config (site apps
      // above, autocapture, toolbar) but PostHog skips evaluating flags, and
      // reloadFeatureFlags() becomes a no-op so identify/group calls stop
      // re-requesting them. Safe only while the project has no surveys:
      // survey targeting rides on flag evaluation.
      advanced_disable_feature_flags: true
    });
    const handleRouteChange = () => posthog?.capture("$pageview");
    const handleRouteChangeStart = () => posthog?.capture("$pageleave", {
      $current_url: oldUrlRef.current
    });
    Router.events.on("routeChangeComplete", handleRouteChange);
    Router.events.on("routeChangeStart", handleRouteChangeStart);
    return () => {
      Router.events.off("routeChangeComplete", handleRouteChange);
      Router.events.off("routeChangeStart", handleRouteChangeStart);
    };
  }, []);

  // Warm the browser cache for all anvil animated-PNG assets (working-status
  // indicators + welcome-screen images) so they display instantly when a user
  // opens a Codesmith session. Called here in _app so the prefetch fires on
  // any page load — useEffect already runs after paint so this is non-blocking.
  useAnvilPrefetch();

  // Warm the intro promo video for first-time Codesmith visitors so the
  // modal's clip plays instantly when they open the Codesmith tab.
  useCodesmithIntroVideoPrefetch();

  // Long-lived tabs quietly pick up new deploys (background reload while
  // hidden, or a full page load on the next navigation).
  useFrontendVersionRefresh();

  // Chart web vitals and client transition duration per route in Grafana.
  useWebVitalsReporting();
  useNavigationTimingReporting();
  useEffect(() => {
    captureAppFirstTouchUtmCampaignFromUrl();
    captureAppFirstTouchRdtCidFromUrl();
  }, []);
  useEffect(() => {
    if (!extensionEmbed) return;
    const handleAuthComplete = (event: MessageEvent) => {
      if (event.origin === window.location.origin && event.data === CODESMITH_EXTENSION_AUTH_MESSAGE) {
        window.location.reload();
      }
    };
    window.addEventListener("message", handleAuthComplete);
    if (window.opener) {
      window.opener.postMessage(CODESMITH_EXTENSION_AUTH_MESSAGE, "*");
      window.close();
    }
    return () => window.removeEventListener("message", handleAuthComplete);
  }, [extensionEmbed]);

  // Layouts returned here keep their element identity across page navigations,
  // so the app chrome (SidebarLayout) reconciles instead of remounting.
  const getLayout = extensionEmbed ? (page: React.ReactElement) => <OrgUrlSyncProvider>{page}</OrgUrlSyncProvider> : Component.getLayout ?? (page_0 => page_0);
  const content = <QueryClientProvider client={queryClient}>
      <PostHogProvider client={posthog}>
        <TooltipProvider>
          {/* Theming is driven entirely by the `.dark` class. The root
              element's color-scheme is a CONSTANT `light dark` and must never
              change at runtime (inline write or class-keyed CSS): on some
              Chromium builds changing the root's used color-scheme
              re-dispatches the `prefers-color-scheme` change event, and in
              "system" mode next-themes' media listener re-applies the theme in
              a self-sustaining loop — perceived as rapid light/dark flashing.
              Hence enableColorScheme={false}. App-theme correctness for native
              controls and portaled surfaces is handled on <body> instead (see
              the color-scheme block in globals.css). */}
          <ThemeProvider attribute="class" defaultTheme="system" enableColorScheme={false}>
            <ErrorBoundary FallbackComponent={ErrorPage} onError={markNavigationRenderError} resetKeys={[router.asPath]}>
              {isCopyPromptRoute || isCanvasSandboxRoute ? <Component {...pageProps} /> : <AuthProvider>
                  <AppearanceSettingsSync />
                  <SsoBlockedOrgGate>
                    {getLayout(<Component {...pageProps} />)}
                  </SsoBlockedOrgGate>
                </AuthProvider>}
            </ErrorBoundary>
            {/* Render inside ThemeProvider so the toaster reads the resolved
                app theme; otherwise useTheme() falls back to "system" and the
                toast surface can mismatch the page theme. */}
            <Toaster />
            <McpOAuthResultBridge />
          </ThemeProvider>
        </TooltipProvider>
        {/* The React Query Devtools floating toggle (the flower) shows in
            development by default. Opt out locally with
            NEXT_PUBLIC_ENABLE_RQ_DEVTOOLS=false (e.g. in .env.development.local,
            which is gitignored). */}
        {process.env.NEXT_PUBLIC_ENABLE_RQ_DEVTOOLS !== "false" && <ReactQueryDevtools initialIsOpen={false} />}
      </PostHogProvider>
    </QueryClientProvider>;
  return <>
      <Dv360FloodlightGlobal />
      {isProductionOrStaging() && process.env.NEXT_PUBLIC_GTM_ID && <>
          <Script id="gtm-init" strategy="afterInteractive">
            {`window.dataLayer = window.dataLayer || [];
if (typeof window.gtag !== 'function') { window.gtag = function(){window.dataLayer.push(arguments);} }
window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});`}
          </Script>
          <Script id="gtm-script" strategy="afterInteractive" src={`https://www.googletagmanager.com/gtm.js?id=${process.env.NEXT_PUBLIC_GTM_ID}`} />
          {/* GTM's Conversion Linker rewrites outbound <a> hrefs at click time,
              appending a _gl tracking param (cross-domain linking). Inside the
              authenticated dashboard that decoration is pure noise on links the
              agent or users share (Grafana, GitHub, docs), so undo it on every
              link as soon as the linker touches the href. */}
          <Script id="gtm-strip-gl" strategy="afterInteractive">
            {`(function() {
  function stripGl(a) {
    try {
      var u = new URL(a.href);
      if (u.searchParams.has('_gl')) {
        u.searchParams.delete('_gl');
        a.href = u.toString();
      }
    } catch(e) {}
  }
  new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var el = mutations[i].target;
      if (el.nodeName === 'A') stripGl(el);
    }
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['href'] });
  document.addEventListener('mousedown', function(e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (a) setTimeout(function() { stripGl(a); }, 0);
  }, true);
})();`}
          </Script>
        </>}
      <style jsx global>{`
        :root {
          --font-berkeley-mono: ${berkeleyMonoVariable.style.fontFamily};
          --font-inter: ${inter.style.fontFamily};
          --font-ibm-plex-sans: ${ibmPlexSans.style.fontFamily};
          --font-pixel: ${departureMono.style.fontFamily};
        }
      `}</style>
      {/* next/font only injects @font-face when each font's className is mounted. */}
      <span className={cn(ibmPlexSans.className, "sr-only")} aria-hidden />
      <main className={cn(inter.className, "h-full overflow-hidden")}>
        {content}
        <SpeedInsights />
      </main>
    </>;
}